import PropTypes from 'prop-types';
import equals from "ramda/es/equals";
import reject from "ramda/es/reject";
import cx from "ramda-extension/es/cx";
import isFunction from "ramda-extension/es/isFunction";
import React, { cloneElement, Component } from 'react';
import { withDefaultComponentProps } from '../../../decorators/withDefaultComponentProps';
import FormGroup from '../../../molecules/FormGroup';
import { validationDisplayTypePropType } from '../../../molecules/Validation/propTypes';
import Input from '../Input';
import SimpleInput from '../SimpleTextField/SimpleInput';
import textFieldComposer from '../TextFieldComposer';
import Textarea from '../Textarea';
const COMPONENT_NAME = 'TextField';
const isValid = (value)=>value != null && value !== '';
const inputControlClassName = 'Input-control';
const placeholderClassName = `${inputControlClassName}--showPlaceholder`;
export const nativeInputType = {
    TEXT: 'text',
    EMAIL: 'email',
    URL: 'url',
    NUMBER: 'number',
    TEL: 'tel'
};
class InputElement extends Component {
    shouldComponentUpdate(nextProps) {
        return !equals(reject(isFunction, this.props), reject(isFunction, nextProps));
    }
    render() {
        const { controlInputClassName, showPlaceholder, inputRef, inputElementRef, ...rest } = this.props;
        return /*#__PURE__*/ React.createElement("input", {
            ...rest,
            ref: (el)=>{
                if (inputRef) {
                    inputRef.current = el;
                }
                if (inputElementRef) {
                    inputElementRef(el);
                }
            },
            className: cx(inputControlClassName, controlInputClassName, {
                [placeholderClassName]: showPlaceholder
            })
        });
    }
}
InputElement.displayName = 'InputElement';
class TextField extends Component {
    constructor(props){
        super(props);
        const { children } = props;
        const propsLeaf = children ? children.props : props;
        this.state = {
            hasValue: isValid(propsLeaf.value)
        };
    }
    static getDerivedStateFromProps(nextProps, state) {
        let propsLeaf;
        if (nextProps.children?.props) {
            propsLeaf = nextProps.children.props;
        } else {
            propsLeaf = nextProps;
        }
        if (propsLeaf.value !== undefined && propsLeaf.value !== null) {
            const hasValue = isValid(propsLeaf.value);
            if (hasValue !== state.hasValue) {
                return {
                    hasValue
                };
            }
        }
        return null;
    }
    uncontrolledOnBlur = (e)=>{
        if (this.props.value === undefined || this.props.value === null) {
            this.setState({
                hasValue: e.target.value !== ''
            });
        }
        this.props.controlProps.onBlur(e);
    };
    render() {
        const { multiline, children, type, controlInputClassName, controlTextareaClassName, placeholder, rows, asyncValidationInProgress, controlProps, inputProps, isFocused, value, simple, sizeSmall, tooltip, outline, onBlur, ...other } = this.props;
        let inputElement;
        let hasPlaceholder;
        let showPlaceholder;
        const hasFloatingLabel = inputProps?.floatingLabelText;
        if (children) {
            hasPlaceholder = isValid(children.props.placeholder);
            showPlaceholder = !hasFloatingLabel || isFocused && hasPlaceholder;
            inputElement = /*#__PURE__*/ cloneElement(children, {
                ...controlProps,
                ...children.props,
                className: cx(inputControlClassName, controlInputClassName, {
                    [placeholderClassName]: showPlaceholder
                }, children.className)
            });
        } else if (multiline) {
            hasPlaceholder = isValid(placeholder) || controlProps && isValid(controlProps.placeholder);
            showPlaceholder = !hasFloatingLabel || isFocused && hasPlaceholder;
            inputElement = /*#__PURE__*/ React.createElement(Textarea, {
                ...other,
                ...controlProps,
                onBlur: this.uncontrolledOnBlur,
                className: cx(inputControlClassName, controlTextareaClassName, {
                    [placeholderClassName]: showPlaceholder
                }),
                rows: rows
            });
        } else {
            hasPlaceholder = isValid(placeholder) || controlProps && isValid(controlProps.placeholder);
            showPlaceholder = !hasFloatingLabel || isFocused && hasPlaceholder;
            const inputElementProps = {
                ...other,
                ...controlProps,
                onBlur: this.uncontrolledOnBlur,
                controlInputClassName,
                showPlaceholder,
                type
            };
            inputElement = /*#__PURE__*/ React.createElement(InputElement, inputElementProps);
        }
        const InputType = simple ? SimpleInput : Input;
        return /*#__PURE__*/ React.createElement(InputType, {
            floatingLabelShrink: this.state.hasValue || isFocused,
            multiline: multiline,
            tooltip: tooltip,
            asyncValidationInProgress: asyncValidationInProgress,
            value: value,
            outline: outline,
            sizeSmall: sizeSmall,
            ...inputProps
        }, inputElement);
    }
}
TextField.propTypes = {
    /**
	 * True if async validation is in progress
	 */ asyncValidationInProgress: PropTypes.bool,
    /**
	 * Disable / enable native autocomple on input element (values: `on`, `off`).
	 */ autoComplete: PropTypes.string,
    /**
	 * Optionally provide own input element as component children.
	 */ children: PropTypes.node,
    /**
	 * Class of FormGroup container.
	 */ className: PropTypes.string,
    /**
	 * Set class for control. Control is component in between actual input/textarea and TextField.
	 */ controlClassName: PropTypes.string,
    /**
	 * Set classname for input, when input is used. This class will also be used for custom children.
	 */ controlInputClassName: PropTypes.string,
    /**
	 * Set classname for textarea, if multiline is true.
	 */ controlTextareaClassName: PropTypes.string,
    /**
	 * Whether control should be disabled. Disabled elements are not editable, focusable, and are not sent with form when submitting.
	 */ disabled: PropTypes.bool,
    /**
	 * Set class for floating label.
	 */ floatingLabelClassName: PropTypes.string,
    /**
	 * Set floating label. Floating label is displayed in place of control text. When control gets focused or has value, the label "floats" away, but is still visible.
	 */ floatingLabelText: PropTypes.node,
    /**
	 * Whether to render validation component.
	 */ hideValidationText: PropTypes.bool,
    /**
	 * Id of the form element (input / textarea). If not specified, one will be generated automatically to connect input with label using the `for` attribute.
	 */ id: PropTypes.string,
    /**
	 * Set class of static label.
	 */ labelClassName: PropTypes.string,
    /**
	 * Static label note text. Label note is directly attached to label text, and displayed bellow with more subtle style.
	 */ labelNote: PropTypes.node,
    /**
	 * Position of label (`left`, `right`). By default `left` is used, which leads to displaying label on top of the input. When right is used, label will be displayed right of the input.
	 */ labelPosition: PropTypes.oneOf(FormGroup.labelPositions.values()),
    /**
	 * Static label text.
	 */ labelText: PropTypes.node,
    /**
	 * If set to true, textarea will be used as control.
	 */ multiline: PropTypes.bool,
    /**
	 * Control focus lost callback
	 *
	 * @param {object} event event targeting the element.
	 * @return {undefined} Nothing
	 */ onBlur: PropTypes.func,
    /**
	 * Control value changed callback
	 *
	 * @param {object} event Event targeting the element.
	 * @param {object} value New control value
	 * @return {undefined} Nothing
	 */ onChange: PropTypes.func,
    /**
	 * Control clicked callback
	 *
	 * @param {object} event event targeting the element.
	 * @return {undefined} Nothing
	 */ onClick: PropTypes.func,
    /**
	 * Control focused callback
	 *
	 * @param {object} event event targeting the element.
	 * @return {undefined} Nothing
	 */ onFocus: PropTypes.func,
    /**
	 * Flag indicating whether to user outlined input or not
	 */ outline: PropTypes.bool,
    /**
	 * TODO !!
	 */ panelControls: PropTypes.node,
    /**
	 * Native placeholder. The prop will be passed to input/textarea, but also to custom children - meaning we can specify placeholder directly on TextField component and not inside children.
	 */ placeholder: PropTypes.string,
    /**
	 * Whether control should be readonly. Styling is more subtle than when disabled is used. Readonly elements can be focused and gets sent when form submits.
	 */ readOnly: PropTypes.bool,
    /**
	 * Show whether Component is required
	 */ required: PropTypes.bool,
    /**
	 * In case when multiline is used, set number of textarea rows.
	 */ rows: PropTypes.number,
    /**
	 * If true, TextField will be rendered using SimpleInput
	 */ simple: PropTypes.bool,
    /**
	 * If true, Input will be rendered with sizeSmall modifier
	 */ sizeSmall: PropTypes.bool,
    /**
	 * Tooltip children, either string or custom element
	 */ tooltip: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ]),
    /**
	 * Various native input types (number, email, tel, url...).
	 */ type: PropTypes.string,
    /**
	 * Set class for validation component.
	 */ validationClassName: PropTypes.string,
    /**
	 * Where to display validation
	 */ validationDisplayType: validationDisplayTypePropType,
    /**
	 * Text that will be shown when withValidity decorator decides to show validation text.
	 */ validationText: PropTypes.node,
    /**
	 * Control value
	 *
	 * @param {object} event event targeting the element.
	 * @return {undefined} Nothing
	 */ value: PropTypes.any
};
const TextFieldWithDefaultProps = withDefaultComponentProps(COMPONENT_NAME, {}, TextField);
const composedTextFieldWithoutValidations = textFieldComposer(TextFieldWithDefaultProps, false);
export { TextField, composedTextFieldWithoutValidations };
export default textFieldComposer(TextFieldWithDefaultProps);
